"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Megaphone, PartyPopper } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ApiError, api, type SharePrefs } from "@/lib/api";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { formatMoney, type MoneyCurrency } from "@/lib/format";

interface WinShareModalProps {
  // The win data — caller fills these from the resolved position.
  win: {
    amountCents: number;
    marketTitle: string;
    marketSlug: string;
    outcome: "YES" | "NO";
    winType: "small" | "big" | "milestone";
  };
  username: string;
  onClose: () => void;
}

/**
 * EPIC-006 STORY-610 — post-win share modal.
 *
 * Three actions: copy link, copy image, share to X. Honors
 * STORY-611's per-user share_prefs (suppressed if promptsEnabled is
 * false; the privacy toggles drive the image's content).
 *
 * The image source is `/api/share/win?...` (STORY-609) — server-
 * rendered PNG via @vercel/og. The modal embeds an `<img>` of the
 * same URL so the user previews exactly what they'll share.
 */
export function WinShareModal({
  win,
  username,
  onClose,
}: WinShareModalProps) {
  const { t } = useTranslation();
  const displayCurrency = useDisplayCurrency();
  const [prefs, setPrefs] = useState<SharePrefs | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [imageCopying, setImageCopying] = useState(false);
  // EPIC-006 STORY-612 — bridge into EPIC-022 post feed. We track
  // posting + the resulting post id so we can show "View post" instead
  // of "Post to feed" once the user clicks.
  const [postingToFeed, setPostingToFeed] = useState(false);
  const [postedId, setPostedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .getSharePrefs()
      .then((p) => {
        if (!cancelled) setPrefs(p);
      })
      .catch(() => {
        if (!cancelled) setPrefs(SHARE_PREFS_DEFAULTS);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!prefs) return null;

  // Build the image URL from prefs — privacy toggles control the
  // content the renderer paints.
  const imageUrl = buildImageUrl({
    win,
    username,
    prefs,
  });
  const marketUrl = absoluteUrl(`/markets/${win.marketSlug}`);
  const tweetText = buildTweetText({ win, prefs, username, displayCurrency });

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(marketUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch {
      setError(t("winShareModal.errCopyLink"));
    }
  }

  async function handleCopyImage() {
    setImageCopying(true);
    setError(null);
    try {
      const res = await fetch(imageUrl);
      if (!res.ok) throw new Error(t("winShareModal.errImageFetch"));
      const blob = await res.blob();
      // Browsers without ClipboardItem support fall through to a
      // download instead.
      if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({ [blob.type]: blob }),
        ]);
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `granbay-win-${Date.now()}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : t("winShareModal.errCopyImage"),
      );
    } finally {
      setImageCopying(false);
    }
  }

  function handleShareToX() {
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(marketUrl)}`;
    window.open(tweetUrl, "_blank", "noopener,noreferrer");
  }

  /**
   * EPIC-006 STORY-612 — post the win to the user's feed (EPIC-022).
   *
   * Composes a markdown body that includes the share-card image, the
   * win amount (privacy-gated by prefs.showAmount), the market title,
   * and an outcome line. The post lands at `/posts/[id]` and surfaces
   * on `/feed/posts` for everyone who follows the user.
   */
  async function handlePostToFeed() {
    if (postingToFeed || postedId || !prefs) return;
    setPostingToFeed(true);
    setError(null);
    try {
      const amountLine = prefs.showAmount
        ? `**+${formatMoney(win.amountCents, displayCurrency)}** on `
        : "Won on ";
      const titleLine = prefs.showMarketTitle
        ? `**${win.marketTitle}**`
        : "a market";
      // Markdown renderer (apps/api/src/posts/markdown.ts) doesn't
      // support images — only safe-list tags. Reference the share
      // card via a plain link; the in-feed post renders text + a
      // "See market →" CTA. The image-rich version is what gets
      // shared externally via Copy image / Share to X.
      const bodyMd = [
        `🎉 ${amountLine}${titleLine} — ${win.outcome}.`,
        "",
        `[See market →](${marketUrl})`,
      ].join("\n");
      const post = await api.createPost({
        bodyMd,
        visibility: "public",
      });
      setPostedId(post.id);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("winShareModal.errPostFeed"),
      );
    } finally {
      setPostingToFeed(false);
    }
  }

  if (typeof document === "undefined") return null;
  // Portal to <body> — sidesteps backdrop-filter ancestors that
  // would re-root our `fixed inset-0` containing block.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="win-share-title"
      className="fixed inset-0 z-50 flex items-center justify-center px-3 py-4"
    >
      <button
        type="button"
        aria-label={t("winShareModal.dismiss")}
        onClick={onClose}
        className="absolute inset-0 bg-black/70"
      />
      <div className="relative max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-xl">
        <div className="flex items-baseline justify-between">
          <h2
            id="win-share-title"
            className="flex items-center gap-2 text-[16px] font-bold text-[var(--color-text)]"
          >
            <PartyPopper size={18} strokeWidth={1.75} aria-hidden="true" />
            {t("winShareModal.title")}
          </h2>
          <Link
            href="/settings/profile"
            className="text-[10px] uppercase tracking-[0.4px] text-[var(--color-text3)] hover:text-[var(--color-text)]"
          >
            {t("winShareModal.privacyLink")}
          </Link>
        </div>

        <p className="mt-1 text-[12px] text-[var(--color-text3)]">
          {t("winShareModal.subtitle")}
        </p>

        {/* Preview image */}
        <div className="mt-3 overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-bg)]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageUrl}
            alt={t("winShareModal.previewAlt")}
            className="w-full"
            style={{ aspectRatio: "1200 / 630" }}
          />
        </div>

        {error && (
          <p
            role="alert"
            className="mt-2 text-[11px]"
            style={{ color: "var(--color-no)" }}
          >
            {error}
          </p>
        )}

        <div className="mt-4 grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={handleCopyLink}
            className="min-h-[44px] rounded-md border border-[var(--color-border)] py-2 text-[11px] font-bold text-[var(--color-text2)] hover:border-[var(--color-brand)] md:min-h-0"
          >
            {linkCopied
              ? t("winShareModal.copyLinkDone")
              : t("winShareModal.copyLink")}
          </button>
          <button
            type="button"
            disabled={imageCopying}
            onClick={handleCopyImage}
            className="min-h-[44px] rounded-md border border-[var(--color-border)] py-2 text-[11px] font-bold text-[var(--color-text2)] hover:border-[var(--color-brand)] disabled:opacity-50 md:min-h-0"
          >
            {imageCopying
              ? t("winShareModal.copyImageBusy")
              : t("winShareModal.copyImage")}
          </button>
          <button
            type="button"
            onClick={handleShareToX}
            className="min-h-[44px] rounded-md py-2 text-[11px] font-bold text-white md:min-h-0"
            style={{ background: "#000000" }}
          >
            {t("winShareModal.shareToX")}
          </button>
        </div>

        {/* EPIC-006 STORY-612 — bridge into EPIC-022 post feed. */}
        <div className="mt-2">
          {postedId ? (
            <Link
              href={`/posts/${postedId}`}
              className="flex min-h-[44px] w-full items-center justify-center rounded-md py-2 text-center text-[11px] font-bold text-white transition-colors md:min-h-0"
              style={{ background: "var(--color-brand)" }}
            >
              {t("winShareModal.postedView")}
            </Link>
          ) : (
            <button
              type="button"
              onClick={handlePostToFeed}
              disabled={postingToFeed}
              className="min-h-[44px] w-full rounded-md border py-2 text-[11px] font-bold transition-colors disabled:opacity-50 md:min-h-0"
              style={{
                borderColor: "var(--color-brand)",
                color: "var(--color-brand)",
                background: "var(--color-brand-tint)",
              }}
            >
              {postingToFeed ? (
                t("winShareModal.postToFeedBusy")
              ) : (
                <span className="inline-flex items-center justify-center gap-1.5">
                  <Megaphone size={12} strokeWidth={2} aria-hidden="true" />
                  {t("winShareModal.postToFeed")}
                </span>
              )}
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mt-3 min-h-[44px] w-full rounded-md border border-[var(--color-border)] py-2 text-[11px] font-bold text-[var(--color-text3)] hover:text-[var(--color-text)] md:min-h-0"
        >
          {t("winShareModal.skip")}
        </button>
      </div>
    </div>,
    document.body,
  );
}

const SHARE_PREFS_DEFAULTS: SharePrefs = {
  promptsEnabled: true,
  showUsername: true,
  showAmount: true,
  showMarketTitle: true,
};

function buildImageUrl(args: {
  win: {
    amountCents: number;
    marketTitle: string;
    outcome: "YES" | "NO";
    winType: "small" | "big" | "milestone";
  };
  username: string;
  prefs: SharePrefs;
}): string {
  const params = new URLSearchParams({
    amount: String(args.win.amountCents),
    market: args.win.marketTitle,
    outcome: args.win.outcome,
    winType: args.win.winType,
    username: args.username,
    showAmount: String(args.prefs.showAmount),
    showUsername: String(args.prefs.showUsername),
    showMarket: String(args.prefs.showMarketTitle),
  });
  return `/api/share/win?${params.toString()}`;
}

function buildTweetText(args: {
  win: { amountCents: number; outcome: "YES" | "NO" };
  prefs: SharePrefs;
  username: string;
  displayCurrency: MoneyCurrency;
}): string {
  if (!args.prefs.showAmount) {
    return `Just won on ${args.win.outcome} on @granbay 🎉`;
  }
  // Drop the cents (round-down to whole units) and show the currency
  // symbol matching the user's display preference. Pre-STORY-2215
  // this hardcoded "R$X" — fine for a Brazilian-only beachhead, but
  // leaks Brazilian-specific copy into share intents posted from
  // non-BR accounts.
  const wholeUnits = Math.floor(args.win.amountCents / 100);
  const formatted = formatMoney(wholeUnits * 100, args.displayCurrency)
    .replace(/[.,]\d{2}$/, '');
  return `Just won ${formatted} on ${args.win.outcome} on @granbay 🎉`;
}

function absoluteUrl(path: string): string {
  if (typeof window !== "undefined") {
    return `${window.location.origin}${path}`;
  }
  return path;
}
